import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Webhook, Plus, X, RefreshCw, Check, Trash2, Play, ChevronDown,
  ChevronUp, Copy, AlertCircle, Clock, Activity, ToggleLeft, ToggleRight,
  Shield, Eye, EyeOff, Link2, Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_EVENTS = [
  "user.created", "user.updated", "user.deleted",
  "business.created", "business.updated",
  "order.created", "order.updated", "order.completed",
  "payment.completed", "payment.failed",
  "agent.message", "support.ticket.created", "support.ticket.updated",
];

const METHOD_COLORS = {
  GET: "bg-green-500/20 text-green-400",
  POST: "bg-blue-500/20 text-blue-400",
  PUT: "bg-yellow-500/20 text-yellow-400",
  DELETE: "bg-red-500/20 text-red-400",
  PATCH: "bg-purple-500/20 text-purple-400",
};

function WebhookCard({ wh, onDelete, onToggle, onTest }) {
  const [expanded, setExpanded] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await axios.get(`${API}/webhooks/${wh.id}/logs`);
      setLogs(r.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) loadLogs();
    setExpanded(!expanded);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/webhooks/${wh.id}/test`);
      setTestResult(r.data);
      if (r.data.success) toast.success("Webhook enviado exitosamente");
      else toast.error(`Error: ${r.data.error || r.data.status_code}`);
    } catch (e) {
      setTestResult({ success: false, error: e.message });
      toast.error("Error al enviar webhook");
    } finally {
      setTesting(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(wh.url);
    toast.success("URL copiada");
  };

  const copySecret = () => {
    navigator.clipboard.writeText(wh.secret || "");
    toast.success("Secret copiado");
  };

  return (
    <div className="glass-panel overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              wh.is_active ? "bg-green-500/20" : "bg-gray-500/20"
            }`}>
              <Webhook size={18} className={wh.is_active ? "text-green-400" : "text-gray-500"} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium">{wh.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-gray-500 text-xs font-mono truncate max-w-xs">{wh.url}</p>
                <button onClick={copyUrl} className="text-gray-600 hover:text-gray-400 flex-shrink-0">
                  <Copy size={11} />
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            <span className={`badge text-xs ${wh.is_active ? "badge-success" : "badge-error"}`}>
              {wh.is_active ? "Activo" : "Inactivo"}
            </span>
            <button
              onClick={() => onToggle(wh.id, wh.is_active)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Activar/Desactivar"
            >
              {wh.is_active ? <ToggleRight size={16} className="text-green-400" /> : <ToggleLeft size={16} />}
            </button>
            <button
              onClick={handleTest}
              disabled={testing}
              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
              title="Probar webhook"
            >
              {testing ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
            </button>
            <button
              onClick={() => onDelete(wh.id)}
              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
            <button onClick={handleExpand} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Events */}
        <div className="flex flex-wrap gap-1">
          {(wh.events || []).map(ev => (
            <span key={ev} className="badge badge-primary text-xs">{ev}</span>
          ))}
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-3 p-3 rounded-lg text-xs ${testResult.success ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
            <div className="flex items-center gap-2 mb-1">
              {testResult.success ? <Check size={12} className="text-green-400" /> : <AlertCircle size={12} className="text-red-400" />}
              <span className={testResult.success ? "text-green-400" : "text-red-400"}>
                {testResult.success ? `Exitoso (HTTP ${testResult.status_code})` : `Error: ${testResult.error}`}
              </span>
            </div>
            {testResult.response && (
              <p className="text-gray-500 font-mono truncate">{testResult.response}</p>
            )}
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-white/5 p-5 space-y-4">
          {/* Secret */}
          <div>
            <p className="text-gray-400 text-xs mb-1.5 font-medium">Firma Secreta (HMAC-SHA256)</p>
            <div className="flex items-center gap-2 bg-black/30 rounded-lg p-2">
              <Shield size={13} className="text-yellow-400 flex-shrink-0" />
              <code className="text-yellow-300 text-xs flex-1 font-mono">
                {showSecret ? (wh.secret || "Sin secret") : "••••••••••••••••••••••••"}
              </code>
              <button onClick={() => setShowSecret(!showSecret)} className="text-gray-500 hover:text-gray-300">
                {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
              <button onClick={copySecret} className="text-gray-500 hover:text-gray-300">
                <Copy size={12} />
              </button>
            </div>
            <p className="text-gray-600 text-[10px] mt-1">Header enviado: <code className="text-gray-500">X-OkBot-Signature</code></p>
          </div>

          {/* Logs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs font-medium">Historial de Entregas</p>
              <button onClick={loadLogs} className="text-gray-500 hover:text-gray-300 text-xs flex items-center gap-1">
                <RefreshCw size={10} className={loadingLogs ? "animate-spin" : ""} />Actualizar
              </button>
            </div>
            {loadingLogs ? (
              <div className="flex items-center justify-center py-4"><RefreshCw size={16} className="animate-spin text-gray-500" /></div>
            ) : logs.length === 0 ? (
              <p className="text-gray-600 text-xs text-center py-3">Sin entregas registradas</p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {logs.map((log, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${log.success ? "bg-green-500/8" : "bg-red-500/8"}`}>
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${log.success ? "bg-green-400" : "bg-red-400"}`} />
                    <span className="text-gray-400">{log.event || "test"}</span>
                    <span className={log.success ? "text-green-400" : "text-red-400"}>{log.status_code || (log.success ? "200" : "Err")}</span>
                    <span className="text-gray-600 ml-auto">{log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateWebhookModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", url: "", events: [], secret: "" });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleEvent = (ev) => {
    if (form.events.includes(ev)) set("events", form.events.filter(e => e !== ev));
    else set("events", [...form.events, ev]);
  };

  const handleCreate = async () => {
    if (!form.name || !form.url) { toast.error("Nombre y URL son requeridos"); return; }
    if (!form.url.startsWith("https://")) { toast.error("La URL debe usar HTTPS"); return; }
    if (form.events.length === 0) { toast.error("Selecciona al menos un evento"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/webhooks`, form);
      toast.success("Webhook creado exitosamente");
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear webhook");
    } finally {
      setSaving(false);
    }
  };

  const eventGroups = [
    { label: "Usuarios", events: ["user.created", "user.updated", "user.deleted"] },
    { label: "Negocios", events: ["business.created", "business.updated"] },
    { label: "Órdenes", events: ["order.created", "order.updated", "order.completed"] },
    { label: "Pagos", events: ["payment.completed", "payment.failed"] },
    { label: "Agentes & Soporte", events: ["agent.message", "support.ticket.created", "support.ticket.updated"] },
  ];

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg animate-scale-in flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <div>
            <h3 className="text-white font-semibold text-lg">Nuevo Webhook</h3>
            <p className="text-gray-500 text-xs mt-0.5">Configura un endpoint para recibir eventos de OkBot</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre *</label>
            <input
              type="text" value={form.name} onChange={e => set("name", e.target.value)}
              placeholder="Ej: Notificaciones Bubble.io"
              className="input-glass w-full"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">URL del Endpoint *</label>
            <input
              type="url" value={form.url} onChange={e => set("url", e.target.value)}
              placeholder="https://tu-app.com/webhook/okbot"
              className="input-glass w-full"
            />
            <p className="text-gray-600 text-xs mt-1">Debe comenzar con https://</p>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Secret (opcional)</label>
            <input
              type="text" value={form.secret} onChange={e => set("secret", e.target.value)}
              placeholder="Dejar vacío para generar automáticamente"
              className="input-glass w-full font-mono"
            />
            <p className="text-gray-600 text-xs mt-1">Se usa para verificar la firma HMAC-SHA256 del header <code>X-OkBot-Signature</code></p>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium">
              Eventos a escuchar * ({form.events.length} seleccionados)
            </label>
            <button
              onClick={() => set("events", ALL_EVENTS)}
              className="text-red-400 text-xs hover:underline mb-2"
            >
              Seleccionar todos
            </button>
            <div className="space-y-3">
              {eventGroups.map(group => (
                <div key={group.label}>
                  <p className="text-gray-600 text-xs mb-1.5 font-medium">{group.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.events.map(ev => (
                      <label key={ev} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                        form.events.includes(ev)
                          ? "bg-red-500/20 text-red-300 border border-red-500/30"
                          : "bg-white/5 text-gray-400 hover:bg-white/10"
                      }`}>
                        <input
                          type="checkbox"
                          checked={form.events.includes(ev)}
                          onChange={() => toggleEvent(ev)}
                          className="sr-only"
                        />
                        {ev}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 pt-4 border-t border-white/10">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleCreate} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Webhook size={14} />}
            Crear Webhook
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WebhooksManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/webhooks`);
      setWebhooks(r.data || []);
    } catch {
      toast.error("Error al cargar webhooks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este webhook? Los eventos en cola se perderán.")) return;
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success("Webhook eliminado");
      setWebhooks(w => w.filter(x => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      const r = await axios.put(`${API}/webhooks/${id}`, {
        ...webhooks.find(w => w.id === id),
        is_active: !currentActive
      });
      if (r.data.success !== false) {
        setWebhooks(w => w.map(x => x.id === id ? { ...x, is_active: !currentActive } : x));
        toast.success(`Webhook ${!currentActive ? "activado" : "desactivado"}`);
      }
    } catch {
      load();
    }
  };

  const handleCreated = (newWh) => {
    setWebhooks(w => [newWh, ...w]);
    setShowCreate(false);
    toast.success("Webhook creado y listo para recibir eventos");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={24} className="animate-spin text-red-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Webhook size={22} className="text-red-400" />Webhooks
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Recibe notificaciones en tiempo real cuando ocurren eventos en OkBot
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-black/30 rounded-xl px-3 py-2 text-xs text-gray-400">
            <span className="text-white font-medium">{webhooks.filter(w => w.is_active).length}</span> activos de {webhooks.length}
          </div>
          <button onClick={load} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={16} />Nuevo Webhook
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="glass-panel p-4 mb-6 border-blue-500/20 bg-blue-500/5">
        <div className="flex items-start gap-3">
          <Shield size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-400 space-y-1">
            <p><span className="text-white font-medium">Verificación de firma:</span> Cada webhook incluye el header <code className="text-blue-300">X-OkBot-Signature</code> firmado con HMAC-SHA256.</p>
            <p><span className="text-white font-medium">Header de evento:</span> <code className="text-blue-300">X-OkBot-Event</code> indica el tipo de evento enviado.</p>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Webhook size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 font-medium mb-2">No hay webhooks configurados</p>
          <p className="text-gray-600 text-sm mb-6">Crea tu primer webhook para recibir eventos en tiempo real</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mx-auto flex items-center gap-2">
            <Plus size={16} />Crear Webhook
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map(wh => (
            <WebhookCard
              key={wh.id}
              wh={wh}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onTest={() => {}}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateWebhookModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
